// === src/components/WATareasStats.js ===
import React, { useState, useEffect, useMemo } from "react";
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    Box,
    Typography,
    Grid,
    Paper,
    IconButton,
    Divider,
    CircularProgress,
} from "@mui/material";
import {
    Close as CloseIcon,
    ArrowBack as ArrowBackIcon,
    ArrowForward as ArrowForwardIcon,
    TrendingUp as TrendingUpIcon,
    TrendingDown as TrendingDownIcon,
} from "@mui/icons-material";
import { PieChart } from "@mui/x-charts/PieChart";
import { BarChart } from "@mui/x-charts/BarChart";
import dayjs from "dayjs";
import "dayjs/locale/es";

dayjs.locale("es");

export default function WATareasStats({ open, onClose, tareas }) {
    const [mesActual, setMesActual] = useState(dayjs());

    // Filtrar tareas del mes seleccionado
    const tareasMes = useMemo(() => {
        if (!tareas || !Array.isArray(tareas)) return [];

        return tareas.filter((t) => {
            if (!t.fecha) return false;
            const fechaTarea = dayjs(t.fecha);
            return (
                fechaTarea.year() === mesActual.year() &&
                fechaTarea.month() === mesActual.month()
            );
        });
    }, [tareas, mesActual]);

    // Calcular estadísticas
    const stats = useMemo(() => {
        const total = tareasMes.length;

        // Por estado
        const resueltas = tareasMes.filter((t) => t.estado === "resuelto").length;
        const pendientes = tareasMes.filter((t) => t.estado === "pendiente").length;
        const canceladas = tareasMes.filter((t) => t.estado === "cancelada").length;

        // Medicina Laboral (visible_para_sector contiene "16")
        const tareasML = tareasMes.filter((t) =>
            String(t.visible_para_sector || "")
                .split(",")
                .map((v) => v.trim())
                .includes("16")
        );
        const aprobadas = tareasML.filter((t) => t.resolucion === "aprobado").length;
        const rechazadas = tareasML.filter((t) => t.resolucion === "rechazado").length;
        const resueltasRRHH = tareasML.filter((t) =>
            t.estado === "resuelto" &&
            (!t.resolucion || (t.resolucion !== "aprobado" && t.resolucion !== "rechazado"))
        ).length;

        // 🆕 Tiempo promedio de respuesta de Medicina Laboral
        const tareasMLConTiempo = tareasML.filter(
            (t) => (t.resolucion === "aprobado" || t.resolucion === "rechazado") && t.fecha && t.fecha_accion
        );

        let tiempoPromedioMLHoras = 0;
        if (tareasMLConTiempo.length > 0) {
            const sumaHoras = tareasMLConTiempo.reduce((acc, t) => {
                const inicio = dayjs(t.fecha);
                const fin = dayjs(t.fecha_accion);
                return acc + fin.diff(inicio, "hour", true);
            }, 0);
            tiempoPromedioMLHoras = sumaHoras / tareasMLConTiempo.length;
        }

        // Formatear tiempo promedio (horas o días)
        let tiempoPromedioMLTexto = "-";
        if (tiempoPromedioMLHoras > 0) {
            if (tiempoPromedioMLHoras < 24) {
                tiempoPromedioMLTexto = `${tiempoPromedioMLHoras.toFixed(1)}h`;
            } else {
                const dias = tiempoPromedioMLHoras / 24;
                tiempoPromedioMLTexto = `${dias.toFixed(1)}d`;
            }
        }

        // Archivos - CORREGIDO
        // 1. Archivos enviados DIRECTAMENTE (sin posponer)
        const archivosDirectos = tareasMes.filter((t) =>
            t.archivo &&
            t.archivo.trim() &&
            !t.fecha_pospuesto_archivo
        ).length;

        // 2. Archivos POSPUESTOS y enviados A TIEMPO (mismo día)
        const archivosPospuestosATiempo = tareasMes.filter((t) => {
            if (!t.archivo || !t.archivo.trim()) return false;
            if (!t.fecha_pospuesto_archivo || !t.fecha_recepcion_archivo) return false;

            const fechaPospuesto = dayjs(t.fecha_pospuesto_archivo);
            const fechaRecepcion = dayjs(t.fecha_recepcion_archivo);

            return fechaPospuesto.format("YYYY-MM-DD") === fechaRecepcion.format("YYYY-MM-DD");
        }).length;

        // 3. Archivos POSPUESTOS y enviados TARDE (otro día)
        const archivosPospuestosTarde = tareasMes.filter((t) => {
            if (!t.archivo || !t.archivo.trim()) return false;
            if (!t.fecha_pospuesto_archivo || !t.fecha_recepcion_archivo) return false;

            const fechaPospuesto = dayjs(t.fecha_pospuesto_archivo);
            const fechaRecepcion = dayjs(t.fecha_recepcion_archivo);

            return fechaPospuesto.format("YYYY-MM-DD") !== fechaRecepcion.format("YYYY-MM-DD");
        }).length;

        // 4. Archivos AÚN PENDIENTES (pospuestos pero no enviados)
        const archivosPendientes = tareasMes.filter((t) =>
            (!t.archivo || !t.archivo.trim()) &&
            t.fecha_pospuesto_archivo &&
            (t.tipo_accion === "archivo_pospuesto" || t.tipo_accion === "esperando_archivo")
        ).length;

        // Totales
        const conArchivo = archivosDirectos + archivosPospuestosATiempo + archivosPospuestosTarde;
        const archivosATiempo = archivosDirectos + archivosPospuestosATiempo; // Directos + pospuestos a tiempo
        const archivosTarde = archivosPospuestosTarde;
        const pospuestos = archivosPospuestosATiempo + archivosPospuestosTarde + archivosPendientes;

        // Tiempos de respuesta (solo resueltas)
        const resueltasConTiempo = tareasMes.filter(
            (t) => t.estado === "resuelto" && t.fecha && t.fecha_accion
        );

        let menosDeUnaHora = 0;
        let unaACuatroHoras = 0;
        let cuatroAVeinticuatroHoras = 0;
        let masDeVeinticuatroHoras = 0;

        resueltasConTiempo.forEach((t) => {
            const inicio = dayjs(t.fecha);
            const fin = dayjs(t.fecha_accion);
            const diffHoras = fin.diff(inicio, "hour", true);

            if (diffHoras < 1) menosDeUnaHora++;
            else if (diffHoras < 4) unaACuatroHoras++;
            else if (diffHoras < 24) cuatroAVeinticuatroHoras++;
            else masDeVeinticuatroHoras++;
        });

        // TOP 5 tipos de consultas
        const tiposCount = {};
        tareasMes.forEach((t) => {
            const tipo = t.tipo_detalle || "Sin categoría";
            tiposCount[tipo] = (tiposCount[tipo] || 0) + 1;
        });

        const top5 = Object.entries(tiposCount)
            .sort((a, b) => b[1] - a[1])
            .slice(0, 5)
            .map(([tipo, count]) => ({ tipo, count }));

        // Mes anterior (para comparación)
        const mesAnterior = mesActual.subtract(1, "month");
        const tareasMesAnterior = tareas.filter((t) => {
            if (!t.fecha) return false;
            const fechaTarea = dayjs(t.fecha);
            return (
                fechaTarea.year() === mesAnterior.year() &&
                fechaTarea.month() === mesAnterior.month()
            );
        });

        const totalMesAnterior = tareasMesAnterior.length;
        const cambioMensual =
            totalMesAnterior > 0 ? ((total - totalMesAnterior) / totalMesAnterior) * 100 : 0;

        const promedioPorDia = total / mesActual.daysInMonth();

        return {
            total,
            resueltas,
            pendientes,
            canceladas,
            aprobadas,
            rechazadas,
            resueltasRRHH,
            totalML: tareasML.length,
            tiempoPromedioMLTexto,
            tiempoPromedioMLHoras,
            conArchivo,
            archivosDirectos,
            archivosPospuestosATiempo,
            archivosPospuestosTarde,
            archivosPendientes,
            archivosATiempo,
            archivosTarde,
            pospuestos,
            menosDeUnaHora,
            unaACuatroHoras,
            cuatroAVeinticuatroHoras,
            masDeVeinticuatroHoras,
            top5,
            totalMesAnterior,
            cambioMensual,
            promedioPorDia,
        };
    }, [tareasMes, tareas, mesActual]);

    const handleMesAnterior = () => {
        setMesActual(mesActual.subtract(1, "month"));
    };

    const handleMesSiguiente = () => {
        setMesActual(mesActual.add(1, "month"));
    };

    const handleMesActualHoy = () => {
        setMesActual(dayjs());
    };

    // Datos para gráfico de torta (Estado)
    const dataPieEstado = [
        { id: 0, value: stats.resueltas, label: "Resueltas", color: "#4caf50" },
        { id: 1, value: stats.pendientes, label: "Pendientes", color: "#ff9800" },
        { id: 2, value: stats.canceladas, label: "Canceladas", color: "#f44336" },
    ];

    // Datos para gráfico de barras (Tiempos de respuesta)
    const dataTiempos = [
        stats.menosDeUnaHora,
        stats.unaACuatroHoras,
        stats.cuatroAVeinticuatroHoras,
        stats.masDeVeinticuatroHoras,
    ];

    const labelsTiempos = ["< 1h", "1-4h", "4-24h", "> 24h"];

    return (
        <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
            <DialogTitle
                sx={{
                    bgcolor: "#1976d2",
                    color: "white",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                }}
            >
                <Typography variant="h6">📊 Estadísticas de Gestión por medio del Bot Whatsapp</Typography>
                <IconButton onClick={onClose} sx={{ color: "white" }}>
                    <CloseIcon />
                </IconButton>
            </DialogTitle>

            <DialogContent dividers sx={{ p: 3 }}>
                {/* Navegación de mes */}
                <Box
                    sx={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "space-between",
                        mb: 3,
                    }}
                >
                    <IconButton onClick={handleMesAnterior} size="small">
                        <ArrowBackIcon />
                    </IconButton>

                    <Box sx={{ textAlign: "center" }}>
                        <Typography variant="h6" sx={{ fontWeight: "bold" }}>
                            {mesActual.format("MMMM YYYY").charAt(0).toUpperCase() +
                                mesActual.format("MMMM YYYY").slice(1)}
                        </Typography>
                        <Button size="small" onClick={handleMesActualHoy} sx={{ mt: 0.5 }}>
                            Mes actual
                        </Button>
                    </Box>

                    <IconButton onClick={handleMesSiguiente} size="small">
                        <ArrowForwardIcon />
                    </IconButton>
                </Box>

                {stats.total === 0 ? (
                    <Box sx={{ textAlign: "center", py: 4 }}>
                        <Typography variant="body1" color="text.secondary">
                            No hay tareas registradas en este mes
                        </Typography>
                    </Box>
                ) : (
                    <Grid container spacing={2}>
                        {/* 📥 TAREAS RECIBIDAS */}
                        <Grid item xs={12}>
                            <Paper elevation={2} sx={{ p: 2 }}>
                                <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
                                    📥 TAREAS RECIBIDAS
                                </Typography>
                                <Divider sx={{ mb: 2 }} />

                                <Grid container spacing={2}>
                                    <Grid item xs={4}>
                                        <Box>
                                            <Typography variant="caption" color="text.secondary">
                                                Este mes
                                            </Typography>
                                            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                                                <Typography variant="h5" fontWeight="bold">
                                                    {stats.total}
                                                </Typography>
                                                {stats.cambioMensual !== 0 && (
                                                    <Box
                                                        sx={{
                                                            display: "flex",
                                                            alignItems: "center",
                                                            color:
                                                                stats.cambioMensual > 0
                                                                    ? "success.main"
                                                                    : "error.main",
                                                        }}
                                                    >
                                                        {stats.cambioMensual > 0 ? (
                                                            <TrendingUpIcon fontSize="small" />
                                                        ) : (
                                                            <TrendingDownIcon fontSize="small" />
                                                        )}
                                                        <Typography variant="caption">
                                                            {Math.abs(stats.cambioMensual).toFixed(0)}%
                                                        </Typography>
                                                    </Box>
                                                )}
                                            </Box>
                                        </Box>
                                    </Grid>

                                    <Grid item xs={4}>
                                        <Typography variant="caption" color="text.secondary">
                                            Mes anterior
                                        </Typography>
                                        <Typography variant="h5" fontWeight="bold">
                                            {stats.totalMesAnterior}
                                        </Typography>
                                    </Grid>

                                    <Grid item xs={4}>
                                        <Typography variant="caption" color="text.secondary">
                                            Promedio/día
                                        </Typography>
                                        <Typography variant="h5" fontWeight="bold">
                                            {stats.promedioPorDia.toFixed(1)}
                                        </Typography>
                                    </Grid>
                                </Grid>
                            </Paper>
                        </Grid>

                        {/* ✅ RESOLUCIÓN - con gráfico */}
                        <Grid item xs={12} md={6}>
                            <Paper elevation={2} sx={{ p: 2, height: "100%" }}>
                                <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
                                    ✅ RESOLUCIÓN
                                </Typography>
                                <Divider sx={{ mb: 2 }} />

                                <Box sx={{ display: "flex", flexDirection: "column", gap: 1, mb: 2 }}>
                                    <Box sx={{ display: "flex", justifyContent: "space-between" }}>
                                        <Typography variant="body2">Resueltas</Typography>
                                        <Typography variant="body2" fontWeight="bold">
                                            {stats.resueltas} (
                                            {stats.total > 0
                                                ? ((stats.resueltas / stats.total) * 100).toFixed(0)
                                                : 0}
                                            %)
                                        </Typography>
                                    </Box>

                                    <Box sx={{ display: "flex", justifyContent: "space-between" }}>
                                        <Typography variant="body2">Pendientes</Typography>
                                        <Typography variant="body2" fontWeight="bold">
                                            {stats.pendientes} (
                                            {stats.total > 0
                                                ? ((stats.pendientes / stats.total) * 100).toFixed(0)
                                                : 0}
                                            %)
                                        </Typography>
                                    </Box>

                                    <Box sx={{ display: "flex", justifyContent: "space-between" }}>
                                        <Typography variant="body2">Canceladas</Typography>
                                        <Typography variant="body2" fontWeight="bold">
                                            {stats.canceladas} (
                                            {stats.total > 0
                                                ? ((stats.canceladas / stats.total) * 100).toFixed(0)
                                                : 0}
                                            %)
                                        </Typography>
                                    </Box>
                                </Box>

                                {/* Gráfico de torta */}
                                <Box sx={{ display: "flex", justifyContent: "center" }}>
                                    <PieChart
                                        series={[
                                            {
                                                data: dataPieEstado,
                                                highlightScope: { faded: "global", highlighted: "item" },
                                                faded: { innerRadius: 30, additionalRadius: -10 },
                                            },
                                        ]}
                                        width={280}
                                        height={180}
                                        slotProps={{
                                            legend: { hidden: true },
                                        }}
                                    />
                                </Box>
                            </Paper>
                        </Grid>

                        {/* 🏥 MEDICINA LABORAL */}
                        <Grid item xs={12} md={6}>
                            <Paper elevation={2} sx={{ p: 2, height: "100%" }}>
                                <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
                                    🏥 MEDICINA LABORAL
                                </Typography>
                                <Divider sx={{ mb: 2 }} />

                                {stats.totalML === 0 ? (
                                    <Typography variant="body2" color="text.secondary">
                                        Sin tareas de Medicina Laboral este mes
                                    </Typography>
                                ) : (
                                    <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
                                        <Box sx={{ display: "flex", justifyContent: "space-between" }}>
                                            <Typography variant="body2">Total ML</Typography>
                                            <Typography variant="body2" fontWeight="bold">
                                                {stats.totalML}
                                            </Typography>
                                        </Box>

                                        <Divider sx={{ my: 0.5 }} />

                                        <Box sx={{ display: "flex", justifyContent: "space-between" }}>
                                            <Typography variant="body2">✅ Aprobadas</Typography>
                                            <Typography variant="body2" fontWeight="bold" color="success.main">
                                                {stats.aprobadas} (
                                                {stats.totalML > 0
                                                    ? ((stats.aprobadas / stats.totalML) * 100).toFixed(0)
                                                    : 0}
                                                %)
                                            </Typography>
                                        </Box>

                                        <Box sx={{ display: "flex", justifyContent: "space-between" }}>
                                            <Typography variant="body2">❌ Rechazadas</Typography>
                                            <Typography variant="body2" fontWeight="bold" color="error.main">
                                                {stats.rechazadas} (
                                                {stats.totalML > 0
                                                    ? ((stats.rechazadas / stats.totalML) * 100).toFixed(0)
                                                    : 0}
                                                %)
                                            </Typography>
                                        </Box>

                                        <Box sx={{ display: "flex", justifyContent: "space-between" }}>
                                            <Typography variant="body2">📋 Resueltas por RRHH</Typography>
                                            <Typography variant="body2" fontWeight="bold">
                                                {stats.resueltasRRHH} (
                                                {stats.totalML > 0
                                                    ? ((stats.resueltasRRHH / stats.totalML) * 100).toFixed(0)
                                                    : 0}
                                                %)
                                            </Typography>
                                        </Box>

                                        <Divider sx={{ my: 0.5 }} />

                                        <Box sx={{ display: "flex", justifyContent: "space-between" }}>
                                            <Typography variant="body2">⏱️ Tiempo promedio ML</Typography>
                                            <Typography
                                                variant="body2"
                                                fontWeight="bold"
                                                sx={{
                                                    color: stats.tiempoPromedioMLHoras > 24 ? 'warning.main' :
                                                        stats.tiempoPromedioMLHoras > 48 ? 'error.main' :
                                                            'text.primary'
                                                }}
                                            >
                                                {stats.tiempoPromedioMLTexto}
                                            </Typography>
                                        </Box>
                                    </Box>
                                )}
                            </Paper>
                        </Grid>

                        {/* 📁 ARCHIVOS ADJUNTOS */}
                        <Grid item xs={12} md={6}>
                            <Paper elevation={2} sx={{ p: 2 }}>
                                <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
                                    📁 ARCHIVOS ADJUNTOS
                                </Typography>
                                <Divider sx={{ mb: 2 }} />

                                <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
                                    <Box sx={{ display: "flex", justifyContent: "space-between" }}>
                                        <Typography variant="body2">Total con archivo</Typography>
                                        <Typography variant="body2" fontWeight="bold">
                                            {stats.conArchivo}
                                        </Typography>
                                    </Box>

                                    <Box sx={{ display: "flex", justifyContent: "space-between" }}>
                                        <Typography variant="body2">Enviados a tiempo</Typography>
                                        <Typography variant="body2" fontWeight="bold" color="success.main">
                                            {stats.archivosATiempo} (
                                            {stats.conArchivo > 0
                                                ? ((stats.archivosATiempo / stats.conArchivo) * 100).toFixed(
                                                    0
                                                )
                                                : 0}
                                            %)
                                        </Typography>
                                    </Box>

                                    <Box sx={{ display: "flex", justifyContent: "space-between" }}>
                                        <Typography variant="body2">Enviados tarde</Typography>
                                        <Typography variant="body2" fontWeight="bold" color="error.main">
                                            {stats.archivosTarde} (
                                            {stats.conArchivo > 0
                                                ? ((stats.archivosTarde / stats.conArchivo) * 100).toFixed(0)
                                                : 0}
                                            %)
                                        </Typography>
                                    </Box>

                                    <Box sx={{ display: "flex", justifyContent: "space-between" }}>
                                        <Typography variant="body2">Usaron código 99</Typography>
                                        <Typography variant="body2" fontWeight="bold">
                                            {stats.pospuestos}
                                        </Typography>
                                    </Box>
                                </Box>
                            </Paper>
                        </Grid>

                        {/* ⏱️ TIEMPOS DE RESPUESTA - con gráfico */}
                        <Grid item xs={12} md={6}>
                            <Paper elevation={2} sx={{ p: 2 }}>
                                <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
                                    ⏱️ TIEMPOS DE RESPUESTA
                                </Typography>
                                <Divider sx={{ mb: 2 }} />

                                {stats.resueltas === 0 ? (
                                    <Typography variant="body2" color="text.secondary">
                                        Sin tareas resueltas este mes
                                    </Typography>
                                ) : (
                                    <Box>
                                        <Box sx={{ display: "flex", flexDirection: "column", gap: 1, mb: 2 }}>
                                            <Box sx={{ display: "flex", justifyContent: "space-between" }}>
                                                <Typography variant="body2">{"< 1 hora"}</Typography>
                                                <Typography variant="body2" fontWeight="bold">
                                                    {stats.menosDeUnaHora} (
                                                    {stats.resueltas > 0
                                                        ? (
                                                            (stats.menosDeUnaHora / stats.resueltas) *
                                                            100
                                                        ).toFixed(0)
                                                        : 0}
                                                    %)
                                                </Typography>
                                            </Box>

                                            <Box sx={{ display: "flex", justifyContent: "space-between" }}>
                                                <Typography variant="body2">1-4 horas</Typography>
                                                <Typography variant="body2" fontWeight="bold">
                                                    {stats.unaACuatroHoras} (
                                                    {stats.resueltas > 0
                                                        ? (
                                                            (stats.unaACuatroHoras / stats.resueltas) *
                                                            100
                                                        ).toFixed(0)
                                                        : 0}
                                                    %)
                                                </Typography>
                                            </Box>

                                            <Box sx={{ display: "flex", justifyContent: "space-between" }}>
                                                <Typography variant="body2">4-24 horas</Typography>
                                                <Typography variant="body2" fontWeight="bold">
                                                    {stats.cuatroAVeinticuatroHoras} (
                                                    {stats.resueltas > 0
                                                        ? (
                                                            (stats.cuatroAVeinticuatroHoras /
                                                                stats.resueltas) *
                                                            100
                                                        ).toFixed(0)
                                                        : 0}
                                                    %)
                                                </Typography>
                                            </Box>

                                            <Box sx={{ display: "flex", justifyContent: "space-between" }}>
                                                <Typography variant="body2">{"> 24 horas"}</Typography>
                                                <Typography variant="body2" fontWeight="bold">
                                                    {stats.masDeVeinticuatroHoras} (
                                                    {stats.resueltas > 0
                                                        ? (
                                                            (stats.masDeVeinticuatroHoras /
                                                                stats.resueltas) *
                                                            100
                                                        ).toFixed(0)
                                                        : 0}
                                                    %)
                                                </Typography>
                                            </Box>
                                        </Box>

                                        {/* Gráfico de barras */}
                                        <Box sx={{ display: "flex", justifyContent: "center" }}>
                                            <BarChart
                                                xAxis={[{ scaleType: "band", data: labelsTiempos }]}
                                                series={[{ data: dataTiempos, color: "#1976d2" }]}
                                                width={280}
                                                height={150}
                                                margin={{ top: 10, bottom: 30, left: 25, right: 10 }}
                                            />
                                        </Box>
                                    </Box>
                                )}
                            </Paper>
                        </Grid>

                        {/* 🔝 TOP 5 TIPOS DE CONSULTAS */}
                        <Grid item xs={12}>
                            <Paper elevation={2} sx={{ p: 2 }}>
                                <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
                                    🔝 TOP 5 TIPOS DE CONSULTAS
                                </Typography>
                                <Divider sx={{ mb: 2 }} />

                                {stats.top5.length === 0 ? (
                                    <Typography variant="body2" color="text.secondary">
                                        Sin consultas este mes
                                    </Typography>
                                ) : (
                                    <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
                                        {stats.top5.map((item, idx) => (
                                            <Box
                                                key={idx}
                                                sx={{
                                                    display: "flex",
                                                    justifyContent: "space-between",
                                                    alignItems: "center",
                                                }}
                                            >
                                                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                                                    <Typography variant="body2" fontWeight="bold">
                                                        {idx + 1}.
                                                    </Typography>
                                                    <Typography variant="body2">{item.tipo}</Typography>
                                                </Box>
                                                <Typography variant="body2" fontWeight="bold">
                                                    {item.count}
                                                </Typography>
                                            </Box>
                                        ))}
                                    </Box>
                                )}
                            </Paper>
                        </Grid>
                    </Grid>
                )}
            </DialogContent>

            <DialogActions>
                <Button onClick={onClose} color="inherit">
                    Cerrar
                </Button>
            </DialogActions>
        </Dialog>
    );
}